import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { ApiWallet, ApiWalletTransaction } from '../../../../api/hexa/ApiWallet';
import WalletTransactionList from './WalletTransactionList';
import SaveButtonComponent from '../../../components/SaveButton';
import React from 'react';
import { BreadcrumbsTitleContext } from '../../../../wrappers/AppWrapper';
import {
  Wallet,
  WalletFormProps,
  WalletTransaction,
} from '../../../../api/hexa/models/WalletModels';

const DEFAULT_FORM = {
  credit: '',
  balance: 0,
  on_demand: false,
  created: '',
};

const DEFAULT_TRANSACTION_FORM = {
  amount: 0,
  transaction_type: 'credit' as 'credit' | 'expenses',
  description: '',
};

const WalletDetail: React.FC = () => {
  const location = useLocation();
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const [transactionSuccess, setTransactionSuccess] = useState({
    open: false,
    message: '',
  });
  const { setWalletName } = useContext(BreadcrumbsTitleContext);
  const [initialFormValue, setInitialFormValue] = useState<WalletFormProps>(DEFAULT_FORM);
  const [isFormUnchanged, setIsFormUnchanged] = useState(true);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isTransactionLoading, setIsTransactionLoading] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const { t } = useTranslation();
  const { customerId, walletId } = useParams();
  
  const {
    getValues,
    control,
    reset,
    formState: { isValid },
  } = useForm<WalletFormProps>({
    defaultValues: DEFAULT_FORM,
    mode: 'onChange',
  });
  
  const {
    getValues: getTransactionValues,
    control: transactionControl,
    reset: resetTransaction,
    formState: { isValid: isTransactionValid },
  } = useForm({
    defaultValues: DEFAULT_TRANSACTION_FORM,
    mode: 'onChange',
  });
  
  const formValues = useWatch({ control });
  const transactionFormValues = useWatch({ control: transactionControl });

  const fetchWallet = useCallback(async () => {
    setLoading(true);
    try {
      let wallet;
      
      if (customerId && walletId) {
        try {
          wallet = await ApiWallet.show(customerId, walletId);
        } catch (apiError) {
          console.warn('Failed to fetch wallet from API, using location state:', apiError);
          wallet = location.state?.wallet;
        }
      } else {
        wallet = location.state?.wallet;
      }

      if (wallet) {
        const creditName = wallet.credit || `Wallet ${wallet.id}`;
        setWalletName(creditName);
        localStorage.setItem('walletName', creditName);
        
        const formValues: WalletFormProps = {
          credit: wallet.credit ?? '',
          balance: wallet.balance ?? 0,
          on_demand: wallet.on_demand ?? false,
          created: wallet.created 
            ? new Date(wallet.created).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : '',
        };
        
        reset(formValues);
        setInitialFormValue(formValues);
      } else {
        setError({
          open: true,
          message: 'No wallet data available',
        });
      }
    } catch (error: any) {
      setError({
        open: true,
        message: `Error loading wallet: ${error.message ?? 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [location.state, setWalletName, reset, customerId, walletId]);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  /**
   * Check if the form values have changed, used to enable/disable save button
   */
  useEffect(() => {
    setIsFormUnchanged(
      JSON.stringify(formValues) === JSON.stringify(initialFormValue)
    );
  }, [formValues, initialFormValue]);

  const onSubmit = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const values: WalletFormProps = getValues();
      const data: Partial<Wallet> = {
        on_demand: values.on_demand,
      };
      if (!walletId) throw new Error('walletId is undefined');
      await ApiWallet.update(customerId, walletId, data);
      setInitialFormValue(values);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} creating or updating the wallet: 
    ${error.response?.data.error ?? 'Error creating or updating the wallet'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [walletId, customerId, getValues]);

  const onTransactionSubmit = useCallback(async () => {
    if (!customerId || !walletId) return;
    setIsTransactionLoading(true);
    try {
      const transactionValues = getTransactionValues();
      
      const data: Partial<WalletTransaction> = {
        amount: transactionValues.amount,
        transaction_type: transactionValues.transaction_type,
        description: transactionValues.description,
      };
      
      await ApiWalletTransaction.create(customerId, walletId, data);
      resetTransaction();
      setTransactionSuccess({
        open: true,
        message: `Transaction ${transactionValues.transaction_type === 'credit' ? 'credited' : 'debited'} successfully`,
      });
      
      setRefreshTrigger(prev => prev + 1);
      
      fetchWallet();
    } catch (error: any) {
      setError({
        open: true,
        message: `Error creating transaction: ${error.response?.data.error ?? 'Unknown error'}`,
      });
    } finally {
      setIsTransactionLoading(false);
    }
  }, [walletId, customerId, getTransactionValues, resetTransaction, fetchWallet]);

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  const handleSuccessSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setTransactionSuccess({
      open: false,
      message: transactionSuccess.message,
    });
  };

  const FormDetail = (
    <>
      <CardContent sx={{ overflowY: 'unset' }}>
        <Stack
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1rem',
          }}
        >
          <Controller
            name="credit"
            control={control}
            render={({ field }) => (
              <TextField
                InputProps={{
                  readOnly: true,
                }}
                {...field}
                sx={{ mb: 2 }}
                label={t('wallets.detail.credit')}
              />
            )}
          />

          <Controller
            name={'on_demand'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <TextField
                  sx={{ mb: 2 }}
                  label={t('wallets.detail.on_demand')}
                  value={value ? 'Active' : 'Inactive'}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Switch
                          size="small"
                          checked={!!value}
                          onChange={onChange}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          />

          <Controller
            name="balance"
            control={control}
            render={({ field }) => (
              <TextField
                InputProps={{
                  readOnly: true,
                }}
                {...field}
                sx={{ mb: 2 }}
                label={t('wallets.detail.balance')}
              />
            )}
          />

          <Controller
            name="created"
            control={control}
            render={({ field }) => (
              <TextField
                InputProps={{
                  readOnly: true,
                }}
                {...field}
                sx={{ mb: 2 }}
                label={t('wallets.detail.created')}
              />
            )}
          />
        </Stack>
      </CardContent>
    </>
  );

  const TransactionForm = (
    <>
      <CardHeader
        title={
          <Typography variant="h5">
            {t('wallet_transaction.create.title')}
          </Typography>
        }
      />
      <CardContent>
        <Stack spacing={3}>
          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
            }}
          >
            <Controller
              name="amount"
              control={transactionControl}
              rules={{ 
                required: t('wallet_transaction.create.amount_required'),
                min: { value: 0.01, message: t('wallet_transaction.create.amount_min') }
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type="number"
                  label={t('wallet_transaction.amount')}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="transaction_type"
              control={transactionControl}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t('wallet_transaction.create.transaction_type')}</InputLabel>
                  <Select
                    {...field}
                    label={t('wallet_transaction.create.transaction_type')}
                  >
                    <MenuItem value="credit">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="success.main">{t('wallet_transaction.create.credit_option')}</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="expenses">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="error.main">{t('wallet_transaction.create.expense_option')}</Typography>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Stack>

          <Controller
            name="description"
            control={transactionControl}
            rules={{ required: t('wallet_transaction.create.description_required') }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t('wallet_transaction.description')}
                multiline
                rows={2}
                error={!!error}
                helperText={error?.message}
                placeholder={t('wallet_transaction.create.description_placeholder')}
              />
            )}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => resetTransaction()}
              disabled={isTransactionLoading}
            >
              {t('wallet_transaction.create.reset_button')}
            </Button>
            <Button
              variant="contained"
              onClick={onTransactionSubmit}
              disabled={!isTransactionValid || isTransactionLoading}
              color={transactionFormValues.transaction_type === 'credit' ? 'success' : 'error'}
            >
              {isTransactionLoading ? t('wallet_transaction.create.processing') : 
                transactionFormValues.transaction_type === 'credit' ? t('wallet_transaction.create.credit_button') : t('wallet_transaction.create.expense_button')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </>
  );

  const DetailContent = (
    <Card
      component="form"
      sx={{
        borderRadius: '0.5rem !important',
        paddingX: '1.5rem',
      }}
    >
      <CardHeader
        title={
          <Typography variant={'h3'}>
            {t('wallets.detail.title')}
          </Typography>
        }
      />
        <>
          <Stack direction="row" pr={'1rem'} justifyContent="flex-end">
            <SaveButtonComponent
              isLoading={isLoading}
              isFormUnchanged={isFormUnchanged}
              isValid={isValid}
              onSubmit={onSubmit}
            ></SaveButtonComponent>
          </Stack>
          {FormDetail}
        </>

        {TransactionForm}

        {customerId &&
          <WalletTransactionList customerId={customerId} refreshTrigger={refreshTrigger} />
        }
    </Card>
  );

  return (
    <>
      <Container>
        {DetailContent}
      </Container>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={transactionSuccess.open}
        autoHideDuration={3000}
        onClose={handleSuccessSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSuccessSnackbarClose} severity="success">
          {transactionSuccess.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WalletDetail;